import { h } from "@/lib/dom";
import { button } from "@/components/ui";
import { aiJson } from "@/services/ai.service";
import { extractFileText } from "@/services/material-ocr.service";
import { topicSources, relevantText } from "@/services/source.service";
import { pbErrorMessage } from "@/lib/pocketbase";
import type { MdField } from "./field";

/**
 * Sparkles-Knopf: KI fügt auf Zuruf Markdown ins Dokument ein.
 *
 * Ohne Auswahl darf sie nur an der Cursorposition EINFÜGEN, nie bestehenden
 * Text anfassen. Ist beim Öffnen Text markiert, darf GENAU dieser Abschnitt
 * ersetzt/gekürzt/gelöscht werden – der Rest bleibt tabu.
 * Zugriff auf Materialien/das andere Dokument ist je Anfrage Opt-in (Checkbox),
 * nicht automatisch. Jede Änderung landet als EIN Undo-Schritt in der
 * ohnehin vorhandenen Editor-Historie (Strg+Z) – kein eigener Verlauf nötig.
 */

const SYNTAX_GUIDE = `Verfügbare Markdown-Syntax dieser App (nutze NUR diese, nichts anderes):
- Überschriften: # bis ###
- Aufgaben: - [ ] Text / - [x] Text
- Tabellen: normale Pipe-Tabellen (| Spalte | Spalte |). GENAU EIN Leerzeichen je Zelle, NIE mit Leerzeichen auf gleiche Breite auffüllen/ausrichten (z. B. "| A | Langes Wort |", NICHT "| A          | Langes Wort |") – die optische Ausrichtung übernimmt der Editor selbst
- In Tabellenzellen geht JEDES Element dieser Liste (fett, Formeln, Bilder, Codespans direkt; für mehrzeiligen Inhalt wie Codeblöcke, Callouts oder mehrere Absätze <br> statt eines echten Zeilenumbruchs setzen, z. B. "| \`\`\`js<br>code()\`\`\` | ::: info<br>Text<br>::: |")
- Formeln: $inline$ und $$abgesetzt$$ (KaTeX)
- Callouts: ::: info|warning|example|definition|summary|tafelbild Titel\\nInhalt\\n:::
- Aufklappbar: ::: aufklappbar Titel\\nInhalt\\n:::
- Karteikarte: ::: karteikarte Begriff\\nVorderseite\\n---\\nRückseite\\n:::
- Zwei Spalten: ::: spalten\\nLinks\\n\\n|||\\n\\nRechts\\n:::
- Zitat: ::: zitat [Autor]\\nText\\n::: (Autor optional, steht in der Kopfzeile, NICHT im Text)
- Begriff mit Erklärung (Popup bei Hover/Tippen): [Begriff](def: "Erklärung") – Erklärung IMMER im Titel-Teil (Anführungszeichen), NIE im Linkziel selbst
- Mermaid-Diagramm: \`\`\`mermaid\\n...\`\`\` (Flowchart, Sequenz, Gantt, xychart, etc. – IMMER in Dreifach-Backticks, NIE in :::)
- TikZ-Grafik: ::: tikz\\n...\\n::: (LaTeX/TikZ-Code, wird über Kroki gerendert – IMMER in :::, NIE in Dreifach-Backticks)
- Code: \`\`\`sprache\\n...\`\`\``;

function buildSystemPrompt(hasSelection: boolean, needsOcr: string[]): string {
  const task = hasSelection
    ? "Der Nutzer hat einen Abschnitt markiert. Du darfst NUR diesen Abschnitt ersetzen, kürzen oder leeren " +
      "(löschen) – der Rest des Dokuments existiert nur als Kontext und darf nicht verändert werden. " +
      'Antworte NUR mit JSON: {"mode":"selection","replacement":"<neuer Markdown-Text für genau diesen Abschnitt, ' +
      'leerer String zum Löschen>"}.'
    : "Du darfst NICHTS Bestehendes ändern oder löschen – nur an der aktuellen Cursorposition NEUEN Inhalt " +
      'einfügen. Antworte NUR mit JSON: {"text":"<einzufügender Markdown-Text>"}.';
  const ocrHint = needsOcr.length
    ? `\n\nOhne auslesbaren Text (reiner Scan, OCR noch nicht eingebaut): ${needsOcr.join(", ")}. Bezieht sich die ` +
      'Anweisung eindeutig auf eines dieser Materialien (z. B. "kopiere die Aufgaben aus <Material>"), darfst du ' +
      'NICHTS einfügen/ersetzen und antwortest stattdessen NUR mit {"error":"<kurze Erklärung, dass dafür OCR nötig ' +
      'wäre, noch nicht eingebaut>"}.'
    : "";
  return (
    "Du bist ein Schreibassistent in einem Markdown-Notizeneditor für Schule/Studium. " +
    task +
    "\n\n" +
    SYNTAX_GUIDE +
    "\n\nDu bekommst mehrere mögliche Quellen mitgeliefert (Dokument, ggf. Datei-Inhalt, Materialien, anderes " +
    "Dokument). Entscheide SELBST, welche davon für die Anweisung sinnvoll sind – nicht jede Quelle ist immer " +
    "relevant. Erfinde keine Zahlen/Fakten, die in keiner Quelle stehen." +
    ocrHint
  );
}

interface SelectionResult {
  mode: "selection";
  replacement: string;
  error?: string;
}
interface InsertResult {
  text: string;
  error?: string;
}



/** Text an einer Stelle "tippen" statt schlagartig einsetzen. */
async function typeInto(ta: MdField, offset: number, text: string, onDone?: () => void): Promise<void> {
  // ponytail: Antwort liegt komplett vor und wird nur häppchenweise angezeigt.
  // Echtes Streaming bräuchte SSE im PocketBase-Hook – erst bauen, wenn die
  // Wartezeit bis zum ersten Zeichen wirklich stört.
  const STEP = 24;
  ta.setSelectionRange(offset, offset);
  for (let i = 0; i < text.length; i += STEP) {
    const chunk = text.slice(i, i + STEP);
    ta.insert(chunk);
    ta.emitInput();
    await new Promise((r) => setTimeout(r, 12));
  }
  onDone?.();
}

export interface SparklesBar {
  el: HTMLElement;
  toggle(): void;
  /** Öffnet die Leiste (falls zu) und setzt den Cursor ins Anweisungsfeld – anders als `toggle()` schließt ein Aufruf bei bereits offener Leiste sie NICHT. Für den Slash-Befehl `/sparkles`. */
  open(): void;
}

/**
 * Sparkles als Leiste am unteren Rand des Editors (statt als Popup): links ein
 * Knopf für eine Datei, in der Mitte das Anweisungsfeld, rechts Abschicken –
 * Enter genügt auch. Der Editor bleibt dabei bedienbar, man kann also während
 * der Eingabe im Text scrollen und etwas markieren.
 */
export function createSparklesBar(opts: {
  ta: MdField;
  topicId: string;
  topicTitle: string;
  otherLabel: string;
  otherContent: string;
}): SparklesBar {
  const { ta } = opts;

  let file: File | null = null;
  const fileInput = h("input", {
    type: "file",
    accept: "image/*,.pdf,.txt,.md,.csv",
    style: "display:none",
    onchange: (e: Event) => {
      file = (e.target as HTMLInputElement).files?.[0] ?? null;
      fileChip.textContent = file ? `📎 ${file.name}` : "";
    },
  }) as HTMLInputElement;
  const fileChip = h("span", { class: "sparkles-file-chip" });

  const input = h("textarea", {
    class: "sparkles-input",
    rows: 1,
    placeholder: "Was soll eingefügt werden? (Enter zum Abschicken)",
  }) as HTMLTextAreaElement;

  const status = h("span", { class: "sparkles-status" });
  const sendBtn = button({ icon: "sparkles", variant: "primary", size: "sm", title: "Einfügen", onClick: () => void run() });

  async function run(): Promise<void> {
    const instruction = input.value.trim();
    if (!instruction) return;
    sendBtn.disabled = true;
    status.textContent = "Denkt nach …";
    status.className = "sparkles-status";

    // Erst hier lesen: nach einem Durchlauf steht das gerade Eingefügte als
    // Auswahl da und ein zweiter Auftrag verbessert genau dieses Stück.
    const selStart = ta.selectionStart;
    const selEnd = ta.selectionEnd;
    const hasSelection = selEnd > selStart;
    const selectedText = hasSelection ? ta.value.slice(selStart, selEnd) : "";

    try {
      let fileText = "";
      if (file) fileText = await extractFileText(file);
      // Echte Textebene der PDFs (kostenlos, exakt) statt der schwächeren
      // OCR-only-Materialsuche – dieselbe Quelle wie Lernpfad/Karteikarten.
      const src = await topicSources({ id: opts.topicId, title: opts.topicTitle }).catch(() => null);
      const materialsText = src ? relevantText(src, instruction, 12000) : "";

      const parts: string[] = [`Anweisung: ${instruction}`];
      if (hasSelection) parts.push(`Markierter Abschnitt (nur dieser darf sich ändern):\n"""\n${selectedText}\n"""`);
      const numbered = ta.value.split("\n").map((l, i) => `${i + 1}: ${l}`).join("\n");

      // Geteiltes Zeichenbudget statt vier unabhängiger Deckel (früher je bis
      // 12000/8000): waren Dokument, Datei UND Materialien gleichzeitig groß,
      // sprengte die Summe trotzdem Groqs Minuten-Kontingent (siehe
      // GROQ_SAFE_PROMPT_TOKENS in ai.service.ts). Das Dokument hat Vorrang,
      // Datei/Materialien/anderes Dokument bekommen nur den Rest.
      let budget = 9000;
      const take = (text: string): string => {
        if (!text || budget <= 0) return "";
        const piece = text.slice(0, budget);
        budget -= piece.length;
        return piece;
      };
      parts.push(`Gesamtes Dokument (zeilennummeriert):\n"""\n${take(numbered)}\n"""`);
      if (fileText.trim()) parts.push(`Inhalt der hochgeladenen Datei:\n"""\n${take(fileText)}\n"""`);
      if (materialsText.trim()) parts.push(`Materialien des Themas:\n"""\n${take(materialsText)}\n"""`);
      if (opts.otherContent.trim()) {
        parts.push(`${opts.otherLabel} (anderes Dokument):\n"""\n${take(opts.otherContent)}\n"""`);
      }

      const system = buildSystemPrompt(hasSelection, src?.needsOcr ?? []);
      const user = parts.join("\n\n");
      status.textContent = "Schreibt …";

      if (hasSelection) {
        const res = await aiJson<SelectionResult>(system, user, 3000, "medium");
        if (res.error) throw new Error(res.error);
        const replacement = res.replacement ?? "";
        ta.setSelectionRange(selStart, selEnd);
        ta.erase();
        await typeInto(ta, selStart, replacement);
        ta.setSelectionRange(selStart, selStart + replacement.length);
      } else {
        const res = await aiJson<InsertResult>(system, user, 3000, "medium");
        if (res.error) throw new Error(res.error);
        const text = (res.text ?? "").trim();
        if (!text) throw new Error("Die KI hat keinen Vorschlag geliefert. Anweisung genauer fassen und nochmal versuchen.");
        await typeInto(ta, selStart, "\n" + text + "\n");
      }
      ta.emitInput();
      input.value = "";
      file = null;
      fileChip.textContent = "";
      status.textContent = "✓ Eingefügt – Strg+Z macht es rückgängig.";
      status.className = "sparkles-status ok";
    } catch (err) {
      status.textContent = pbErrorMessage(err);
      status.className = "sparkles-status err";
    } finally {
      sendBtn.disabled = false;
    }
  }

  input.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void run();
    }
    if (e.key === "Escape") el.classList.remove("is-open");
  });

  const el = h(
    "div",
    { class: "sparkles-bar" },
    button({ icon: "upload", variant: "ghost", size: "sm", title: "Datei oder Bild anhängen", onClick: () => fileInput.click() }),
    fileInput,
    h("div", { class: "sparkles-input-wrap" }, input),
    fileChip,
    status,
    sendBtn
  );

  return {
    el,
    toggle() {
      const open = el.classList.toggle("is-open");
      if (open) requestAnimationFrame(() => input.focus());
    },
    open() {
      el.classList.add("is-open");
      requestAnimationFrame(() => input.focus());
    },
  };
}
